
import React from "react";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useAdminSEO } from "@/hooks/useAdminSEO";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { useAnalytics } from "@/hooks/useAnalytics";
import AdminHeader from "@/components/admin/AdminHeader";
import AnalyticsCards from "@/components/admin/AnalyticsCards";
import DashboardWelcome from "@/components/admin/dashboard/DashboardWelcome";
import DashboardActions from "@/components/admin/dashboard/DashboardActions";
import QuickAccessCards from "@/components/admin/dashboard/QuickAccessCards";
import DashboardTabs from "@/components/admin/dashboard/DashboardTabs";
import { CopyFilesToRoot } from "@/components/admin/dashboard/sitemap/CopyFilesToRoot";
import SitemapGenerationButton from "@/components/admin/dashboard/SitemapGenerationButton";

const Dashboard = () => {
  const { isLoading: isCheckingAuth, isAdmin } = useAdminCheck();
  const { posts, isLoading: postsLoading } = useBlogPosts({ limit: 5 });
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useAnalytics();
  
  // Add noindex meta tags for admin pages
  useAdminSEO("Admin Dashboard - Elf Letter Bot");

  // For SSR initial render - show minimal loading state
  if (typeof window === 'undefined') {
    return <div>Loading...</div>;
  }

  // Show loading until auth check completes
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-lg">Loading dashboard...</p>
      </div>
    );
  }

  // If not admin after auth check, should have been redirected by useAdminCheck
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader title="Admin Dashboard" />

      <main className="container mx-auto p-4 md:p-8 space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <DashboardWelcome 
            title="Welcome to the Admin Dashboard" 
            subtitle="Manage your blog content and view analytics" 
          />
          <DashboardActions />
        </div>

        {/* SEO & Site Management Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">SEO & Site Management</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SitemapGenerationButton />
            <CopyFilesToRoot />
          </div>
        </div>

        {/* Quick Access Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Quick Access</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <QuickAccessCards />
          </div>
        </div>

        {/* Analytics Overview */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Analytics Overview</h2>
          <AnalyticsCards 
            analyticsData={analyticsData} 
            analyticsLoading={analyticsLoading} 
            analyticsError={analyticsError} 
            posts={posts}
          />
        </div>

        {/* Detailed Management */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Content & Data Management</h2>
          <DashboardTabs 
            posts={posts}
            postsLoading={postsLoading}
            analyticsData={analyticsData}
            analyticsLoading={analyticsLoading}
            analyticsError={analyticsError}
          />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
